import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
    const { status, login } = useAuth();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    if (status === "authed") {
        const redirect = location.state?.from?.pathname || "/";
        return <Navigate to={redirect} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email || !password) {
            setError("Ingresa tu email y contraseña");
            return;
        }
        setLoading(true);
        try {
            await login(email.trim().toLowerCase(), password);
            toast.success("Bienvenido");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Error al iniciar sesión";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] grid place-items-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex w-14 h-14 rounded-xl bg-slate-900 text-white items-center justify-center font-display font-semibold text-2xl mb-3">
                        C
                    </div>
                    <h1 className="text-2xl font-display font-semibold text-slate-900">Casas Tracking</h1>
                    <p className="text-sm text-slate-500 mt-1">Inicia sesión para continuar</p>
                </div>
                <Card className="border-slate-200 shadow-sm">
                    <CardContent className="p-6">
                        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                            <div>
                                <Label htmlFor="email" className="text-xs">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu@email.com"
                                    data-testid="login-email"
                                />
                            </div>
                            <div>
                                <Label htmlFor="password" className="text-xs">Contraseña</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPwd ? "text" : "password"}
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="login-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd((v) => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600"
                                        tabIndex={-1}
                                    >
                                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>
                            {error && (
                                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="login-error">
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                                data-testid="login-submit"
                            >
                                {loading ? <Loader2 className="animate-spin" size={16} /> : "Entrar"}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
                <p className="text-xs text-center text-slate-400 mt-6">
                    Sesión activa por 24 horas desde el último inicio.
                </p>
            </div>
        </div>
    );
}
